import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Dfns from 'date-fns/fp';
import { format, toZonedTime } from 'date-fns-tz';

import { formatMoneyAsInt } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { useSetError } from '@src/redux/modals';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { isNotMonday } from '@src/utils/date-utils';

import { Page } from '@Components/Page';
import { EmptyState } from '@Components/EmptyState';
import { StretchMode } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { DatePicker } from '@Components/DatePicker';
import { TablePage } from '@Components/TablePage';
import { TableLoading } from '@Components/TableEmptyState';
import { basicRenderer, BPMTable, CellRenderer, TypedHeader } from '@Components/StickyTable';
import { CUTOFF_DATE, DEFAULT_DATE } from '@Pages/CompetitiveMetrics/competitive-metrics-constants';

import { useCompetitorSpots, useStationNames } from './use-competitive-spots-data';

import './CompetitiveSpots.scss';

const ROW_HEIGHT = 35;
const noop = () => undefined;
const COMPETITOR_LABEL = 'Competitor';
const EASTERN = 'America/New_York';

interface SpotRow {
  network: string;
  avail: string;
  creative: string;
  length: number;
  broadcastTime: string;
  broadcastTimeText: string;
  sellingRotation: string;
  netRate: number;
  program: string;
}

const cell = (value: number | string) => <div className='spots-table-cell'>{value}</div>;

const SPOTS_HEADERS: TypedHeader<SpotRow>[] = [
  { label: 'Network', name: 'network', flex: 2, renderer: row => cell(row.network) },
  { label: 'Avail', name: 'avail', width: 100, renderer: row => cell(row.avail) },
  { label: 'Creative', name: 'creative', flex: 3, renderer: row => cell(row.creative) },
  { label: 'Length', name: 'length', width: 100, renderer: row => cell(row.length) },
  {
    label: 'Broadcast Time (ET)',
    name: 'broadcastTimeText',
    flex: 3,
    minFlexWidth: 200,
    renderer: row => cell(row.broadcastTime)
  },
  {
    label: 'Selling Rotation',
    name: 'sellingRotation',
    flex: 2,
    minFlexWidth: 140,
    renderer: row => cell(row.sellingRotation)
  },
  {
    label: 'Est Cost',
    name: 'netRate',
    flex: 1,
    renderer: row => cell(formatMoneyAsInt(row.netRate))
  },
  { label: 'Program', name: 'program', flex: 3, renderer: row => cell(row.program) }
];

// The only numeric total is the cost sum.
const totalsRenderer: CellRenderer<unknown> = props =>
  basicRenderer({
    ...props,
    data: typeof props.data === 'number' ? formatMoneyAsInt(props.data) : props.data
  });

interface SpotsTableProps {
  rows: SpotRow[];
  csvName: string;
  isLoading: boolean;
  hasError: boolean;
  emptyMessage: string;
}

const SpotsTable = ({ rows, csvName, isLoading, hasError, emptyMessage }: SpotsTableProps) => {
  const [filteredRows, setFilteredRows] = useState(rows);

  // Totals follow the filtered rows, so they have to be recomputed whenever a filter changes.
  const totals = useMemo(
    () => ({
      network: 'Total',
      creative: `Count: ${filteredRows.length}`,
      netRate: filteredRows.reduce((sum, { netRate }) => sum + netRate, 0)
    }),
    [filteredRows]
  );

  const renderNoRows = useCallback(
    () => (
      <TableLoading
        isLoading={isLoading}
        error={hasError}
        errorMessage='Unable to load spots data'
        emptyMessage={emptyMessage}
        rowHeight={ROW_HEIGHT}
      />
    ),
    [isLoading, hasError, emptyMessage]
  );

  return (
    <TablePage>
      <BPMTable
        alternateColors
        headers={SPOTS_HEADERS}
        data={rows}
        totals={totals}
        totalsRenderer={totalsRenderer}
        csvDownload={csvName}
        rowHeight={ROW_HEIGHT}
        onFilteredDataChange={setFilteredRows}
        noRowsRenderer={renderNoRows}
      />
    </TablePage>
  );
};

export const CompetitiveSpots = () => {
  const setError = useSetError();
  const companyInfo = useCompanyInfo();

  const [selectedCompetitor, setSelectedCompetitor] = useState<string>();
  const [weeks, setWeeks] = useState({ start: DEFAULT_DATE, end: DEFAULT_DATE });

  useEffect(() => {
    if (companyInfo.competitors) {
      setSelectedCompetitor(companyInfo.default_competitor || companyInfo.competitors[0]);
    }
  }, [companyInfo]);

  const { data: spotsByWeek, error } = useCompetitorSpots(selectedCompetitor);
  const { data: stationNames, error: stationNamesError } = useStationNames();

  // Both queries opt out of the global error dialog, so each failure is raised here once.
  useEffect(() => {
    if (error) {
      setError({
        message: `Failed to load spots data for ${selectedCompetitor}.`,
        reportError: error
      });
    }
  }, [error, selectedCompetitor, setError]);

  useEffect(() => {
    if (stationNamesError) {
      setError({
        message: `Could not fetch station names. ${stationNamesError.message}`,
        reportError: stationNamesError
      });
    }
  }, [stationNamesError, setError]);

  const competitors = companyInfo.competitors ?? [];

  const rows = useMemo(() => {
    if (spotsByWeek === null) {
      return [];
    }
    if (!spotsByWeek || !stationNames) {
      return undefined;
    }
    const validDays = new Set(
      Dfns.eachDayOfInterval({
        start: Dfns.parseISO(weeks.start),
        end: Dfns.parseISO(weeks.end)
      }).map(Dfns.format('yyyy-MM-dd'))
    );
    return Object.entries(spotsByWeek)
      .filter(([week]) => validDays.has(week))
      .flatMap(([, spots]) => spots)
      .map(({ stationName, spotLength, easternSpotTime, ...spot }): SpotRow => {
        const easternTime = toZonedTime(easternSpotTime, EASTERN);
        return {
          ...spot,
          network: stationNames[stationName] || stationName,
          length: spotLength,
          broadcastTime: format(easternTime, 'EEE MMM d, h:mm:ss a', { timeZone: EASTERN }),
          broadcastTimeText: format(easternTime, 'yyyy-MM-dd HH:mm:ss', { timeZone: EASTERN })
        };
      });
  }, [spotsByWeek, stationNames, weeks]);

  const renderBody = () => {
    if (companyInfo.competitors?.length === 0) {
      return <EmptyState title='No competitors configured' />;
    }
    const hasError = Boolean(error || stationNamesError);
    return (
      <SpotsTable
        // Remount once rows arrive so the filtered-rows state (and totals) start from the loaded data.
        key={`${selectedCompetitor}-${weeks.start}-${weeks.end}-${rows ? 'loaded' : 'pending'}`}
        rows={rows ?? []}
        csvName={`Competitive_${selectedCompetitor}_${weeks.start}-${weeks.end}.csv`}
        isLoading={!rows && !hasError}
        hasError={hasError}
        emptyMessage={
          spotsByWeek === null
            ? `No spots data available for ${selectedCompetitor}`
            : 'No spots in the selected date range.'
        }
      />
    );
  };

  return (
    <Page
      title='Competitive Spots'
      pageType='Competitive Spots'
      minHeight='600px'
      actions={
        <div className='competitive-spots-actions'>
          {selectedCompetitor && (
            <div className='competitor-select'>
              <Dropdown
                label={COMPETITOR_LABEL}
                ariaLabel={COMPETITOR_LABEL}
                value={selectedCompetitor}
                options={competitors}
                onChange={setSelectedCompetitor}
                fitMenuToContent
              />
              {/* Hidden copies of the toggle for every competitor share the grid cell, so it is as wide as the widest. */}
              {competitors.map(competitor => (
                <div key={competitor} className='competitor-select-sizer' aria-hidden>
                  <Dropdown
                    label={COMPETITOR_LABEL}
                    value={competitor}
                    options={competitors}
                    onChange={noop}
                    stretch={StretchMode.FIT}
                  />
                </div>
              ))}
            </div>
          )}
          <DatePicker
            range={weeks}
            fullWeeksOnly
            isOutsideRange={date => date > CUTOFF_DATE}
            isDayBlocked={isNotMonday}
            onChange={({ start, end }) => {
              if (start && end) {
                setWeeks({ start, end });
              }
            }}
          />
        </div>
      }
    >
      <div className='competitive-spots-body'>{renderBody()}</div>
    </Page>
  );
};
